import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { MiTab } from "@miplatform/components";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import {
  CustomerPricingDetailsTabs,
  IAssignPriceLevelPayload,
} from "../../CustomerPricingDetails/CustomerPricingDetails.types";
import {
  useCustomerPricingDetailsContext,
  useTabFormRegistration,
} from "../../CustomerPricingDetails/CustomerPricingDetailsProvider";
import { PriceLevelHistory } from "../../components/PriceLevelHistory";
import { PriceLevelSelection } from "../../components/PriceLevelSelection";
import { customerPriceLevelTabSchema } from "../../schema/customerPricingDetails.schema";
import { useCustomerPriceLevelTabViewModel } from "./useCustomerPriceLevelTabViewModel";

const internalTabs = [
  {
    label: "Price Matrix",
    value: CustomerPricingDetailsTabs.PriceMatrix,
  },
  {
    label: "History",
    value: CustomerPricingDetailsTabs.History,
  },
];

export const CustomerPriceLevelTab = () => {
  const { accountDetails, setSaveHandler, setConfirmModalConfig } =
    useCustomerPricingDetailsContext();

  const [activeTab, setActiveTab] = useState<string | number | undefined>(
    CustomerPricingDetailsTabs.PriceMatrix
  );

  const form = useForm<IAssignPriceLevelPayload>({
    mode: "onChange",
    defaultValues: {
      accountId: "",
      priceLevelId: "",
    },
    resolver: zodResolver(customerPriceLevelTabSchema),
  });
  const { control, reset, getValues, setValue, watch } = form;
  const { priceLevelId } = watch();

  const {
    priceLevels,
    categories,
    categoryDrops,
    priceLevelActivities,
    getPriceLevels,
    getCategories,
    getCategoryDrops,
    createAccountPriceLevel,
    getPriceLevelActivities,
    isPriceMatrixLoading,
    isHistoryLoading,
  } = useCustomerPriceLevelTabViewModel();

  const isLoading = isPriceMatrixLoading || isHistoryLoading;

  useTabFormRegistration(form, isLoading);

  useEffect(() => {
    getPriceLevels();
    getCategories();
    getCategoryDrops();
  }, []);

  useEffect(() => {
    if (accountDetails) {
      reset({
        accountId: accountDetails.id,
        priceLevelId: accountDetails.priceLevelId || "",
      });
      getPriceLevelActivities(accountDetails.id);
    }
  }, [accountDetails, reset]);

  useEffect(() => {
    const existingId = accountDetails?.priceLevelId;

    if (!existingId) {
      setConfirmModalConfig(null);
      return;
    }

    const selectedName = priceLevels.find((l) => l.id === priceLevelId)?.name;
    const description = priceLevelId
      ? `You are about to change ${accountDetails.name}'s price level from ${accountDetails.priceLevelName} to ${selectedName}. Are you sure you would like to continue?`
      : `You are about to unassign ${accountDetails.priceLevelName} from ${accountDetails.name}. Are you sure you would like to continue?`;

    setConfirmModalConfig({ description });
  }, [accountDetails, priceLevels, priceLevelId]);

  useEffect(() => {
    setSaveHandler(async () => {
      const values = getValues();
      const isUnassigning = values.priceLevelId === "";

      const priceLevelId = isUnassigning ? null : values.priceLevelId;
      const priceLevelName = isUnassigning
        ? accountDetails?.priceLevelName
        : priceLevels.find((level) => level.id === priceLevelId)?.name;
      const accountName = accountDetails?.name || "this account";
      const successText = priceLevelId
        ? `Successfully assigned Price Level "${priceLevelName}" to ${accountName}.`
        : `Successfully unassigned Price Level "${priceLevelName}" from ${accountName}.`;

      return createAccountPriceLevel(
        { accountId: values.accountId, priceLevelId },
        { success: { text: successText } }
      );
    });
  }, [priceLevels, accountDetails]);

  const options = priceLevels.map((level) => ({
    label: level.name,
    value: level.id,
  }));

  const activePriceLevelIndex = priceLevelId
    ? priceLevels.findIndex((level) => level.id === priceLevelId)
    : null;

  const groupedData = useMemo(() => {
    return categories.map((category) => {
      const categoryDrop = categoryDrops.filter(
        (drop) => drop.categoryId === category.id
      );
      return {
        category,
        categoryDrop,
      };
    });
  }, [categories, categoryDrops]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setValue("priceLevelId", value === "None" ? "" : value, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <ConfigAccordionHeader title="Price Level" disableCollapse>
      <Box display="flex" flexDirection="column" gap={2}>
        <MiTab
          tabs={internalTabs}
          currentTab={activeTab}
          onTabChange={setActiveTab}
          scrollButtons={false}
          borderBottom
        />
        {activeTab === CustomerPricingDetailsTabs.PriceMatrix && (
          <PriceLevelSelection
            handleChange={handleChange}
            control={control}
            options={options}
            isLoading={isPriceMatrixLoading}
            priceLevels={priceLevels}
            groupedData={groupedData}
            activePriceLevelIndex={activePriceLevelIndex}
          />
        )}
        {activeTab === CustomerPricingDetailsTabs.History && (
          <PriceLevelHistory
            isLoading={isHistoryLoading}
            priceLevelActivities={priceLevelActivities}
          />
        )}
      </Box>
    </ConfigAccordionHeader>
  );
};
